"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { X, Sparkles, ArrowRight, Gift } from "lucide-react";
import { useRouter } from "next/navigation";

export function useOfferTooltip(isLandingPage?: boolean) {
  const router = useRouter();
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    if (isLandingPage) {
      setIsDismissed(true);
      return;
    }

    try {
      const hasClosed = sessionStorage.getItem("hasClosedOfferTooltip");
      if (!hasClosed) {
        // Smooth entrance delay after page & navbar animate in
        const timer = setTimeout(() => {
          setIsDismissed(false);
        }, 700);
        return () => clearTimeout(timer);
      }
    } catch (err) {
      console.error("Session storage access error:", err);
    }
  }, [isLandingPage]);

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setIsDismissed(true);
    try {
      sessionStorage.setItem("hasClosedOfferTooltip", "true");
    } catch (err) {
      console.error(err);
    }
  };

  const handleRedirect = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    router.push("/offers/");
    handleClose();
  };

  return { isDismissed, handleClose, handleRedirect };
}

interface TooltipProps {
  isDismissed: boolean;
  onClose: (e?: React.MouseEvent) => void;
  onRedirect: (e?: React.MouseEvent) => void;
}

/**
 * Desktop Design:
 * Renders an elegant white speech-bubble card with a pointer directly beneath the "Offers" nav menu.
 */
export function OfferNavTooltipDesktop({
  isDismissed,
  onClose,
  onRedirect,
}: TooltipProps) {
  return (
    <AnimatePresence>
      {!isDismissed && (
        <motion.div
          initial={{ opacity: 0, y: 14, scale: 0.94 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.94 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-full mt-3.5 left-1/2 -translate-x-1/2 w-[280px] z-[70] pointer-events-auto select-none"
        >
          {/* Triangular pointer directed up to 'Offers' */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 pointer-events-none drop-shadow-[0_-2px_3px_rgba(0,0,0,0.06)] z-10">
            <svg width="18" height="9" viewBox="0 0 18 9" fill="none">
              <path d="M9 0L18 9H0L9 0Z" fill="#ffffff" />
              <path
                d="M0 9L9 0L18 9"
                stroke="#e6decb"
                strokeWidth="1"
                fill="none"
              />
            </svg>
          </div>

          <div
            onClick={onRedirect}
            className="relative bg-white text-[#2a2520] rounded-2xl shadow-[0_18px_45px_rgba(0,0,0,0.18)] border border-[#e6decb] overflow-hidden cursor-pointer group hover:border-[#c9a961] hover:shadow-[0_22px_55px_rgba(0,0,0,0.22)] transition-all duration-300"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center text-[#2a2520]/50 hover:text-[#2a2520] hover:bg-black/5 transition-colors cursor-pointer z-20"
              aria-label="Close offer tooltip"
              title="Close"
            >
              <X size={15} strokeWidth={2.5} />
            </button>

            <div className="p-4 sm:p-5 pr-8">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5a6f3e]/10 text-[#5a6f3e] text-[10px] font-bold tracking-wider uppercase">
                  <Sparkles className="w-3 h-3 text-[#c9a961]" />
                  Offer
                </span>
              </div>
              <h4
                className="text-[17px] font-semibold text-[#2a2520] leading-snug group-hover:text-[#5a6f3e] transition-colors"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                Book 3 Night Escape,
              </h4>
              <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                <span
                  className="text-xs text-[#5a544c] font-medium"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  3rd night at
                </span>
                <span className="inline-block px-2 py-0.5 bg-gradient-to-r from-[#d4af37] to-[#e6c35c] text-[#1c2612] font-black text-xs rounded-sm shadow-xs tracking-wider">
                  50% OFF
                </span>
              </div>

              {/* Bottom CTA */}
              {/* <div className="mt-3 pt-2.5 border-t border-[#f2ece4] flex items-center justify-between text-xs font-semibold text-[#5a6f3e] group-hover:text-[#3d4d27] transition-colors">
                <span style={{ fontFamily: "Jost, sans-serif" }}>
                  Book This Deal
                </span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </div> */}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Mobile Design:
 * Renders a floating, bottom luxury card with touch-friendly layout and instant action button.
 */
export function OfferNavTooltipMobile({
  isDismissed,
  onClose,
  onRedirect,
}: TooltipProps) {
  return (
    <div className="lg:hidden">
      <AnimatePresence>
        {!isDismissed && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-6 sm:w-[360px] z-[950] pointer-events-auto"
          >
            <div
              onClick={onRedirect}
              className="relative bg-white/98 backdrop-blur-md rounded-2xl p-4 shadow-2xl border border-[#d4af37]/40 overflow-hidden cursor-pointer group hover:shadow-[0_20px_50px_rgba(0,0,0,0.2)] transition-all"
            >
              {/* Decorative top accent gradient */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#5a6f3e] via-[#d4af37] to-[#5a6f3e]" />

              {/* Close Button 'X' */}
              <button
                type="button"
                onClick={onClose}
                className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-[#f5f1ec] flex items-center justify-center text-[#2a2520]/70 hover:text-[#2a2520] hover:bg-[#eae3d8] transition-colors cursor-pointer z-20"
                aria-label="Close offer banner"
                title="Close"
              >
                <X size={15} strokeWidth={2.5} />
              </button>

              {/* Main Content Area */}
              <div className="flex items-start gap-3 pr-7">
                {/* Gift Icon Badge */}
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#fbf7ed] to-[#f4ebd0] border border-[#d4af37]/40 flex items-center justify-center text-[#9a7d2e] shrink-0 shadow-xs mt-0.5">
                  <Gift className="w-5 h-5 text-[#9a7d2e]" />
                </div>

                {/* Offer Details - No truncation, natural wrap */}
                <div className="flex-1 min-w-0">
                  <h4
                    className="text-[17px] font-bold text-[#2a2520] leading-snug break-words"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    Book 3 Night Escape
                  </h4>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span
                      className="text-xs text-[#5a544c] font-medium whitespace-nowrap"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    >
                      3rd night at
                    </span>
                    <span className="inline-block px-2 py-0.5 bg-gradient-to-r from-[#d4af37] to-[#e6c35c] text-[#1c2612] font-black text-xs rounded-sm shadow-xs tracking-wider whitespace-nowrap">
                      50% OFF
                    </span>
                  </div>
                </div>
              </div>

              {/* Responsive Bottom Action Bar */}
              {/* <div className="mt-3 pt-2.5 border-t border-[#f2ece4] flex items-center justify-between gap-2">
                <span
                  className="text-xs text-[#5a6f3e] font-semibold flex items-center gap-1 group-hover:text-[#3d4d27] transition-colors"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  Offer
                  <Sparkles className="w-3.5 h-3.5 text-[#c9a961]" />
                </span>

                <span
                  className="inline-flex items-center gap-1.5 bg-[#5a6f3e] text-white text-xs font-semibold px-4 py-1.5 rounded-sm shadow-sm group-hover:bg-[#4a5f2e] transition-colors"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  Book Now
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div> */}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
